import React, { useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"

const formatEuros = cents =>
  new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 })
    .format((cents || 0) / 100)

// EX-E07 — Drift alerts banner by C&R code.
// One row per CR element above the threshold. Collapsible.
const AnalyticsAlertsBanner = ({ alerts }) => {
  const [collapsed, setCollapsed] = useState(false)
  if (!alerts || alerts.length === 0) return null

  const critical = alerts.filter(a => a.severity === "critical").length
  const warn     = alerts.length - critical
  const tone     = critical > 0 ? "rose" : "amber"
  const palette  = {
    rose:  { bg: "bg-rose-50",  border: "border-rose-200",  text: "text-rose-700",  icon: "text-rose-600",  pill: "bg-rose-100"  },
    amber: { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-800", icon: "text-amber-600", pill: "bg-amber-100" },
  }[tone]

  return (
    <section className={`mx-7 mt-4 rounded-xl border ${palette.border} ${palette.bg} ${palette.text}`}>
      <header className="px-4 py-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <FontAwesomeIcon icon="triangle-exclamation" className={palette.icon} />
          <div className="text-[13px] font-semibold truncate">
            {I18n.t("projects.analytics.alerts.title", { count: alerts.length })}
          </div>
          <div className="flex items-center gap-1.5">
            {critical > 0 && (
              <span className={`text-[10.5px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full ${palette.pill}`}>
                {I18n.t("projects.analytics.alerts.critical_count", { count: critical })}
              </span>
            )}
            {warn > 0 && (
              <span className="text-[10.5px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
                {I18n.t("projects.analytics.alerts.warn_count", { count: warn })}
              </span>
            )}
          </div>
        </div>
        <button
          type="button"
          className="text-[12px] underline hover:no-underline"
          onClick={() => setCollapsed(c => !c)}
        >
          {collapsed
            ? I18n.t("projects.analytics.alerts.expand")
            : I18n.t("projects.analytics.alerts.collapse")}
        </button>
      </header>

      {!collapsed && (
        <div className="px-4 pb-3">
          <div className="grid grid-cols-[120px_minmax(0,2fr)_120px_120px_140px_80px] gap-3 px-3 py-2 bg-white/50 border border-slate-200 rounded-lg text-[10.5px] uppercase tracking-wider text-slate-500 font-medium">
            <div>{I18n.t("projects.analytics.alerts.col.code")}</div>
            <div>{I18n.t("projects.analytics.alerts.col.label")}</div>
            <div className="text-right">{I18n.t("projects.analytics.alerts.col.budget")}</div>
            <div className="text-right">{I18n.t("projects.analytics.alerts.col.engaged")}</div>
            <div className="text-right">{I18n.t("projects.analytics.alerts.col.realized")}</div>
            <div className="text-right">{I18n.t("projects.analytics.alerts.col.ratio")}</div>
          </div>
          <div className="divide-y divide-slate-100 bg-white/70 border border-t-0 border-slate-200 rounded-b-lg">
            {alerts.map(a => (
              <div
                key={a.cost_revenue_element_id}
                className="grid grid-cols-[120px_minmax(0,2fr)_120px_120px_140px_80px] gap-3 px-3 py-2 items-center text-[12.5px] text-slate-800"
              >
                <div className="font-mono text-[12px] text-slate-600">{a.code}</div>
                <div className="truncate" title={a.name}>{a.name}</div>
                <div className="text-right tabular-nums">{formatEuros(a.budget_cents)}</div>
                <div className="text-right tabular-nums">{formatEuros(a.engaged_cents)}</div>
                <div className="text-right tabular-nums">{formatEuros(a.realized_cents)}</div>
                <div className={`text-right tabular-nums font-semibold ${a.severity === "critical" ? "text-rose-700" : "text-amber-700"}`}>
                  {a.ratio_pct.toFixed(1)}%
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  )
}

export default AnalyticsAlertsBanner
